import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/Logo_laboratorio_simple_verde-removebg-preview.png';
import { useAuth } from '../context/AuthContext';
import UserMenu from '../components/UserMenu';
import TurnoModal from '../components/TurnoModal';
import TurnoCalendarModal from '../components/TurnoCalendarModal';
import Modal from '../components/Modal';
import { eliminarTurno, listarTurnos, TurnoResponse } from '../api/turnoApi';
import styles from './DashboardPage.module.css';

function formatHora(hora: string): string {
  return hora.slice(0, 5);
}

function today(): string {
  return new Date().toISOString().split('T')[0];
}

const ACTIONS = [
  {
    icon: '➕',
    title: 'Nuevo análisis',
    desc: 'Agregar un nuevo registro de análisis a un paciente',
    route: '/nuevo-analisis',
  },
  {
    icon: '🔍',
    title: 'Buscar análisis',
    desc: 'Buscar registros de análisis por paciente',
    route: '/buscar-analisis',
  },
  {
    icon: '👤',
    title: 'Pacientes',
    desc: 'Ver, crear y editar pacientes registrados',
    route: '/pacientes',
  },
  {
    icon: '🧪',
    title: 'Estudios',
    desc: 'Ver, crear y editar estudios disponibles',
    route: '/estudios',
  },
  {
    icon: '🏥',
    title: 'Laboratorios',
    desc: 'Ver, crear y editar laboratorios',
    route: '/laboratorios',
  },
  {
    icon: '💳',
    title: 'Obras sociales',
    desc: 'Ver, crear y editar obras sociales',
    route: '/obras-sociales',
  },
];

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'ADMIN';

  const [turnos, setTurnos] = useState<TurnoResponse[]>([]);
  const [loadingTurnos, setLoadingTurnos] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTurno, setEditingTurno] = useState<TurnoResponse | null>(null);
  const [confirmDeleteTurno, setConfirmDeleteTurno] = useState<TurnoResponse | null>(null);
  const [deletingTurnoId, setDeletingTurnoId] = useState<string | null>(null);
  const [panelExpanded, setPanelExpanded] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      setLoadingTurnos(false);
      return;
    }
    listarTurnos()
      .then(setTurnos)
      .finally(() => setLoadingTurnos(false));
  }, [isAdmin]);

  function handleTurnoGuardado(turno: TurnoResponse) {
    setTurnos((prev) => {
      const filtrados = prev.filter((t) => t.id !== turno.id);
      if (turno.fecha !== today()) return filtrados;
      return [...filtrados, turno].sort((a, b) => a.hora.localeCompare(b.hora));
    });
  }

  function openEditModal(turno: TurnoResponse) {
    setEditingTurno(turno);
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditingTurno(null);
  }

  async function confirmarEliminarTurno() {
    if (!confirmDeleteTurno) return;
    const id = confirmDeleteTurno.id;
    setConfirmDeleteTurno(null);
    setDeletingTurnoId(id);
    try {
      await eliminarTurno(id);
      setTurnos((prev) => prev.filter((t) => t.id !== id));
    } finally {
      setDeletingTurnoId(null);
    }
  }

  return (
    <div>
      <header className={styles.topbar}>
        <img src={logo} alt="Logo" className={styles.topbarLogo} />
        <p className={styles.topbarTitle}>Laboratorio Sagrado Corazón</p>
        <UserMenu />
      </header>

      <main className={styles.content}>
        <p className={styles.greeting}>
          {user?.genero === 'M' ? 'Bienvenida' : 'Bienvenido'}{user ? `, ${user.nombre}` : ''}
        </p>

        {isAdmin && (
          <div className={styles.turnosPanel}>
            <div className={styles.turnosPanelHeader}>
              <h2
                className={styles.turnosPanelTitle}
                onClick={() => setPanelExpanded((prev) => !prev)}
                role="button"
                tabIndex={0}
                aria-expanded={panelExpanded}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPanelExpanded((prev) => !prev); }
                }}
              >
                <span className={styles.turnosPanelChevron}>{panelExpanded ? '▴' : '▾'}</span>
                Turnos de hoy
                <span className={styles.turnosPanelBadge}>{turnos.length}</span>
              </h2>
              <div className={styles.turnosPanelActions}>
                <button className={styles.turnosPanelSecondaryBtn} onClick={() => setCalendarOpen(true)}>
                  Ver calendario
                </button>
                <button className={styles.turnosPanelBtn} onClick={() => setModalOpen(true)}>
                  + Registrar turno
                </button>
              </div>
            </div>

            {panelExpanded && (
              loadingTurnos ? (
                <p className={styles.turnosLoading}>Cargando turnos...</p>
              ) : turnos.length === 0 ? (
                <p className={styles.turnosEmpty}>No hay turnos registrados para hoy.</p>
              ) : (
                <div className={styles.turnosList}>
                  {turnos.map((t) => (
                    <div key={t.id} className={styles.turnoRow}>
                      <span className={styles.turnoHora}>{formatHora(t.hora)}</span>
                      <span className={styles.turnoPaciente}>{t.pacienteApellido}, {t.pacienteNombre}</span>
                      <span className={styles.turnoDni}>DNI {t.pacienteDni}</span>
                      <div className={styles.turnoActions}>
                        <button
                          className={styles.turnoEditBtn}
                          onClick={() => openEditModal(t)}
                          title="Editar turno"
                        >
                          <i className="bi bi-pencil-fill"></i>
                        </button>
                        <button
                          className={styles.turnoDeleteBtn}
                          onClick={() => setConfirmDeleteTurno(t)}
                          disabled={deletingTurnoId === t.id}
                          title="Eliminar turno"
                        >
                          <i className="bi bi-trash-fill"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        )}

        <div className={styles.grid}>
          {ACTIONS.map((action) => (
            <div
              key={action.title}
              className={styles.card}
              onClick={() => action.route && navigate(action.route)}
              style={action.route ? undefined : { opacity: 0.5, cursor: 'default' }}
            >
              <span className={styles.cardIcon}>{action.icon}</span>
              <h2 className={styles.cardTitle}>{action.title}</h2>
              <p className={styles.cardDesc}>{action.desc}</p>
            </div>
          ))}
        </div>
      </main>

      {isAdmin && (
        <>
          <TurnoModal
            isOpen={modalOpen}
            onClose={closeModal}
            onSaved={handleTurnoGuardado}
            editingTurno={editingTurno}
          />

          <Modal
            isOpen={confirmDeleteTurno !== null}
            onClose={() => setConfirmDeleteTurno(null)}
            title="Eliminar turno"
            width={420}
          >
            <p className={styles.confirmText}>
              ¿Estás seguro de que querés eliminar el turno de{' '}
              <strong>{confirmDeleteTurno?.pacienteApellido}, {confirmDeleteTurno?.pacienteNombre}</strong> del{' '}
              {confirmDeleteTurno && new Date(`${confirmDeleteTurno.fecha}T00:00:00`).toLocaleDateString('es-AR')}{' '}
              a las {confirmDeleteTurno && formatHora(confirmDeleteTurno.hora)} hs?
              Esta acción no se puede deshacer.
            </p>
            <div className={styles.confirmActions}>
              <button className={styles.cancelConfirmBtn} onClick={() => setConfirmDeleteTurno(null)}>
                Cancelar
              </button>
              <button className={styles.deleteConfirmBtn} onClick={confirmarEliminarTurno}>
                Eliminar
              </button>
            </div>
          </Modal>

          <TurnoCalendarModal isOpen={calendarOpen} onClose={() => setCalendarOpen(false)} />
        </>
      )}
    </div>
  );
}
